import React, { createContext, useState, useContext, useEffect } from 'react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { LAMPORTS_PER_SOL } from '@solana/web3.js';

// Create the context
const BalanceContext = createContext();

// Create a provider component
export const BalanceProvider = ({ children }) => {
  const { publicKey, connected } = useWallet();
  const { connection } = useConnection();
  
  // Updated balance structure to match ModularUserProfile
  const [balance, setBalance] = useState({
    jinu: 0,
    sol: 0
  });
  
  // Fetch real SOL balance when wallet is connected
  useEffect(() => {
    const fetchSolBalance = async () => {
      if (connected && publicKey) {
        try {
          // Fetch the actual SOL balance from the connected wallet
          const lamports = await connection.getBalance(publicKey);
          const solBalance = lamports / LAMPORTS_PER_SOL;
          
          // Format to 2 decimal places for display in the balance tab
          const formattedSolBalance = parseFloat(solBalance.toFixed(2));
          
          console.log('Actual SOL balance from wallet:', solBalance);
          console.log('Formatted SOL balance for display:', formattedSolBalance);
          
          // Update SOL balance with the actual wallet balance
          setBalance(prev => ({
            ...prev,
            sol: formattedSolBalance
          }));
          
          // Update token list with the actual wallet balance
          setTokens(prevTokens => 
            prevTokens.map(token => 
              token.symbol === 'SOL' 
                ? {...token, balance: formattedSolBalance} 
                : token
            )
          );
        } catch (error) {
          console.error('Error fetching SOL balance:', error);
        }
      }
    };
    
    // Fetch balance immediately when wallet connects
    fetchSolBalance();
    
    // Set up interval to refresh balance every 5 seconds
    const interval = setInterval(fetchSolBalance, 5000);
    return () => clearInterval(interval);
  }, [connected, publicKey, connection]);
  
  const [tokens, setTokens] = useState([
    { symbol: 'JINU', name: 'JINU Token', balance: 0, price: 0.028 },
    { symbol: 'SOL', name: 'Solana', balance: 0, price: 75.32 },
    { symbol: 'USDC', name: 'USD Coin', balance: 250, price: 1 },
    { symbol: 'BONK', name: 'Bonk', balance: 50000, price: 0.00002 },
    { symbol: 'JUP', name: 'Jupiter', balance: 150, price: 0.85 }
  ]);

  // Update token balance when main balance changes
  const updateBalance = (currency, newBalance) => {
    if (typeof currency !== 'string') {
      console.error('Currency must be a string:', currency);
      return;
    }
    
    const upperCaseCurrency = currency.toUpperCase();
    
    setBalance(prev => ({
      ...prev,
      [currency]: newBalance
    }));
    
    setTokens(prevTokens => 
      prevTokens.map(token => 
        token.symbol === upperCaseCurrency 
          ? {...token, balance: newBalance} 
          : token
      )
    );
  };

  // Add to balance
  const addToBalance = (currency, amount) => {
    if (typeof currency !== 'string') {
      console.error('Currency must be a string:', currency);
      return;
    }
    
    // Ensure amount is a number
    const numericAmount = Number(amount);
    if (isNaN(numericAmount)) {
      console.error('Invalid amount:', amount);
      return;
    }
    
    const lowerCaseCurrency = currency.toLowerCase();
    console.log(`🔄 Adding ${numericAmount} to ${lowerCaseCurrency} balance`);
    
    // Update the balance state with the new amount
    setBalance(prevBalance => {
      const currentValue = prevBalance[lowerCaseCurrency] || 0;
      const newValue = currentValue + numericAmount;
      console.log(`Balance update: ${currentValue} + ${numericAmount} = ${newValue}`);
      
      const newBalance = {
        ...prevBalance,
        [lowerCaseCurrency]: newValue
      };
      
      console.log('New balance object:', newBalance);
      return newBalance;
    });
    
    // Also update the tokens array
    const upperCaseCurrency = currency.toUpperCase();
    setTokens(prevTokens => {
      const updatedTokens = prevTokens.map(token => {
        if (token.symbol === upperCaseCurrency) {
          const newTokenBalance = token.balance + numericAmount;
          console.log(`Token ${token.symbol} balance updated: ${token.balance} + ${numericAmount} = ${newTokenBalance}`);
          return {...token, balance: newTokenBalance};
        }
        return token;
      });
      return updatedTokens;
    });
  };

  // Subtract from balance
  const subtractFromBalance = (currency, amount) => {
    if (typeof currency !== 'string') {
      console.error('Currency must be a string:', currency);
      return false;
    }
    
    // Ensure amount is a number
    const numericAmount = Number(amount);
    if (isNaN(numericAmount)) {
      console.error('Invalid amount:', amount);
      return false;
    }
    
    const lowerCaseCurrency = currency.toLowerCase();
    console.log(`🔄 Subtracting ${numericAmount} from ${lowerCaseCurrency} balance`);
    
    // Check if there's enough balance
    const currentBalance = balance[lowerCaseCurrency] || 0;
    if (currentBalance >= numericAmount) {
      // Update the balance state with the new amount
      setBalance(prevBalance => {
        const newValue = Math.max(0, (prevBalance[lowerCaseCurrency] || 0) - numericAmount);
        console.log(`Balance update: ${prevBalance[lowerCaseCurrency]} - ${numericAmount} = ${newValue}`);
        
        const newBalance = {
          ...prevBalance,
          [lowerCaseCurrency]: newValue
        };
        
        console.log('New balance object after subtraction:', newBalance);
        return newBalance;
      });
      
      // Also update the tokens array
      const upperCaseCurrency = currency.toUpperCase();
      setTokens(prevTokens => {
        const updatedTokens = prevTokens.map(token => {
          if (token.symbol === upperCaseCurrency) {
            const newTokenBalance = Math.max(0, token.balance - numericAmount);
            console.log(`Token ${token.symbol} balance updated: ${token.balance} - ${numericAmount} = ${newTokenBalance}`);
            return {...token, balance: newTokenBalance};
          }
          return token;
        });
        return updatedTokens;
      });
      return true;
    } else {
      console.log(`Insufficient ${lowerCaseCurrency} balance for subtraction: ${currentBalance} < ${numericAmount}`);
      return false;
    }
  };

  // Update a specific token's balance
  const updateTokenBalance = (symbol, newBalance) => {
    setTokens(prevTokens => 
      prevTokens.map(token => 
        token.symbol === symbol 
          ? {...token, balance: newBalance} 
          : token
      )
    );
    
    // If updating JINU or SOL, also update the main balance
    if (symbol === 'JINU') {
      setBalance(prev => ({
        ...prev,
        jinu: newBalance
      }));
    } else if (symbol === 'SOL') {
      setBalance(prev => ({
        ...prev,
        sol: newBalance
      }));
    }
  };

  return (
    <BalanceContext.Provider 
      value={{ 
        balance, 
        tokens, 
        updateBalance, 
        addToBalance, 
        subtractFromBalance,
        updateTokenBalance,
        setTokens
      }}
    >
      {children}
    </BalanceContext.Provider>
  );
};

// Custom hook to use the balance context
export const useBalance = () => {
  const context = useContext(BalanceContext);
  if (context === undefined) {
    throw new Error('useBalance must be used within a BalanceProvider');
  }
  return context;
};

export default BalanceContext;
